import { useEffect, useRef, useCallback } from 'react';

export const useIntelligentPolling = (callback, options = {}) => {
  const {
    baseInterval = 5000,
    maxInterval = 30000,
    backoffRate = 1.5,
    immediate = true,
    enabled = true
  } = options;

  const timeoutRef = useRef(null);
  const currentIntervalRef = useRef(baseInterval);
  const lastActivityRef = useRef(Date.now());
  const lastDataRef = useRef(null);

  const clearCurrentTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const poll = useCallback(async () => {
    if (!enabled) return;

    try {
      const data = await callback();
      
      // Compare avec les données précédentes
      const hasChanged = JSON.stringify(data) !== JSON.stringify(lastDataRef.current);
      lastDataRef.current = data;

      // Ajuste l'intervalle en fonction de l'activité
      if (hasChanged) {
        currentIntervalRef.current = baseInterval;
        lastActivityRef.current = Date.now();
      } else {
        // Augmente progressivement l'intervalle si pas de changement
        currentIntervalRef.current = Math.min(
          currentIntervalRef.current * backoffRate,
          maxInterval
        );
      }
    } catch (error) {
      console.error('Polling error:', error);
      // En cas d'erreur, augmente l'intervalle
      currentIntervalRef.current = Math.min(
        currentIntervalRef.current * backoffRate,
        maxInterval
      );
    }

    timeoutRef.current = setTimeout(poll, currentIntervalRef.current);
  }, [callback, baseInterval, maxInterval, backoffRate, enabled]);

  // Reset l'intervalle quand l'utilisateur interagit
  const resetInterval = useCallback(() => {
    clearCurrentTimeout();
    currentIntervalRef.current = baseInterval;
    timeoutRef.current = setTimeout(poll, baseInterval);
  }, [baseInterval, poll]);

  useEffect(() => {
    if (immediate) {
      poll();
    }

    // Event listeners pour détecter l'activité utilisateur
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > baseInterval) {
        resetInterval();
      }
      lastActivityRef.current = now;
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      clearCurrentTimeout();
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [immediate, poll, resetInterval, baseInterval]);

  return resetInterval;
};